// standards-ignore: co-located-test — presentational-only; no state, hooks, or side effects
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Card } from '@amzn/stencil-react-components/card';
import {
  SkeletonText,
  SkeletonBase,
  SkeletonTextVariant,
} from '@amzn/stencil-react-components/skeleton';

interface CardSkeletonProps {
  /** Number of skeleton lines to show (default: 4 — title, subtitle, badges, meta). */
  lines?: number;
  /** Stable Stencil test ID for integration-test state assertions. */
  dataTestId?: string;
}

/**
 * Generic card skeleton placeholder used during initial page loads.
 * Matches the visual layout of list cards (audits, tasks, etc.) so the
 * transition from skeleton to real content feels seamless.
 *
 * @example
 * ```tsx
 * {isLoading && Array.from({ length: 5 }).map((_, i) => <CardSkeleton key={i} />)}
 * ```
 */
export function CardSkeleton({ lines = 4, dataTestId }: CardSkeletonProps): JSX.Element {
  return (
    <Card padding="dimensions.spacing.300" {...(dataTestId ? { dataTestId } : {})}>
      <Col gridGap="dimensions.spacing.200">
        {/* Title line */}
        <SkeletonText variant={SkeletonTextVariant.HeadingSM} width="70%" />

        {/* Subtitle / secondary info */}
        {lines >= 2 && <SkeletonText variant={SkeletonTextVariant.BodySM} width="50%" />}

        {/* Badge row */}
        {lines >= 3 && (
          <Row gridGap="dimensions.spacing.200">
            <SkeletonBase width={80} height={22} borderRadius={12} />
            <SkeletonBase width={60} height={22} borderRadius={12} />
          </Row>
        )}

        {/* Meta row (assignee + date) */}
        {lines >= 4 && <SkeletonText variant={SkeletonTextVariant.BodySM} width="60%" />}
      </Col>
    </Card>
  );
}
